import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import ArticleBody from "@/pages/detail/components/ArticleBody";
import ShareBar from "@/pages/detail/components/ShareBar";
import RelatedArticles from "@/pages/detail/components/RelatedArticles";
import { useSeo } from "@/hooks/useSeo";
import { categoryPath, formatDate, formatViews } from "@/utils/article";

export default function DetailPage() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);

  useSeo({
    title: article ? `${article.title} - 台北娜娜` : "内容不存在 - 台北娜娜",
    description: article
      ? article.summary
      : "抱歉，你访问的内容不存在或已被移除，请返回首页浏览更多精彩内容。",
    keywords: article
      ? `台北娜娜,${article.categoryName},${article.tags.join(",")}`
      : "台北娜娜,内容不存在",
  });

  if (!article) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-24 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-300" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">内容不存在或已下架</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          抱歉，你访问的内容可能已被移除或链接有误。不妨回到首页，看看我们为你准备的最新内容。
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
          >
            浏览品牌动态
          </Link>
        </div>
      </div>
    );
  }

  const related = articles
    .filter((item) => item.category === article.category && item.id !== article.id)
    .slice(0, 3);

  const popular = articles
    .filter((item) => item.category === article.category && item.id !== article.id)
    .sort((a, b) => b.views - a.views)
    .slice(0, 5);

  return (
    <article className="bg-background-50 px-4 pb-16 pt-24 md:px-8 md:pt-28 lg:px-12">
      <nav className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={categoryPath(article.category)} className="cursor-pointer transition-colors hover:text-primary-600">
          {article.categoryName}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="line-clamp-1 max-w-[220px] text-foreground-700">{article.title}</span>
      </nav>

      <div className="mx-auto mt-6 grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-[1fr_320px] lg:gap-12">
        <div>
          <header>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-price-tag-3-line" />
              {article.categoryName}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 次阅读
              </span>
            </div>
          </header>

          <div className="mt-7 overflow-hidden rounded-2xl border border-background-200">
            <div className="aspect-[16/9] w-full bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 台北娜娜`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="mt-8">
            <ArticleBody blocks={article.blocks} />
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`${categoryPath(article.category)}?tag=${encodeURIComponent(tag)}`}
                className="cursor-pointer rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-8">
            <ShareBar articleId={article.id} title={article.title} />
          </div>

          <RelatedArticles
            articles={related}
            category={article.category}
            categoryName={article.categoryName}
          />

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to={categoryPath(article.category)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回{article.categoryName}
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
            >
              <i className="ri-home-4-line" />
              回到首页
            </Link>
          </div>
        </div>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">文章信息</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">所属分类</dt>
                <dd className="font-medium text-foreground-900">{article.categoryName}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">编辑署名</dt>
                <dd className="font-medium text-foreground-900">{article.author}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">发布日期</dt>
                <dd className="font-medium text-foreground-900">{article.date}</dd>
              </div>
            </dl>
          </div>

          <div className="mt-5 rounded-2xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              本分类热门
            </h2>
            <ol className="mt-4 space-y-4">
              {popular.map((item, index) => (
                <li key={item.id}>
                  <Link to={`/detail/${item.id}`} className="group flex cursor-pointer items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        index === 0
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="line-clamp-2 text-sm leading-snug text-foreground-700 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </article>
  );
}